import React from 'react';

interface CheckboxProps {
  checked?: boolean;
  indeterminate?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  label?: string;
  error?: boolean;
  className?: string;
  id?: string;
  name?: string;
  required?: boolean;
}

export const Checkbox: React.FC<CheckboxProps> = ({
  checked = false,
  indeterminate = false,
  onChange,
  disabled = false,
  label = '',
  error = false,
  className = '',
  id,
  name,
  required = false,
}) => {
  const checkboxId =
    id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled && onChange) {
      onChange(!checked);
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <input
        type="checkbox"
        id={checkboxId}
        name={name}
        checked={checked}
        onChange={() => {}}
        disabled={disabled}
        required={required}
        className="sr-only"
      />
      <button
        type="button"
        role="checkbox"
        aria-checked={indeterminate ? 'mixed' : checked}
        disabled={disabled}
        onClick={handleClick}
        className={`
          flex h-4 w-4 shrink-0 items-center justify-center rounded border-2 transition-colors
          ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
          ${
            error
              ? 'border-red-500 bg-red-50'
              : checked || indeterminate
                ? 'border-indigo-600 bg-indigo-600'
                : 'border-gray-300 bg-white hover:border-gray-400'
          }
          focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1
        `}>
        {checked && !indeterminate && (
          <svg
            className="h-2.5 w-2.5 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="3">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
        )}
        {indeterminate && (
          <svg
            className="h-2.5 w-2.5 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="3">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 12h14"
            />
          </svg>
        )}
      </button>

      {label && (
        <label
          htmlFor={checkboxId}
          onClick={handleClick}
          className={`
            text-sm font-medium cursor-pointer select-none
            ${
              disabled
                ? 'cursor-not-allowed text-gray-400'
                : error
                  ? 'text-red-700'
                  : 'text-gray-900'
            }
          `}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
    </div>
  );
};
